
import React, { useMemo, useState, useEffect } from 'react';
import { NewsItem, Product } from '../types';
import { FileQuestion, Home, ArrowRight, Search, AlertTriangle, Package, Newspaper, Compass, ShoppingBag } from 'lucide-react';

interface NotFoundPageProps {
  brokenSlug: string;
  news: NewsItem[];
  products: Product[];
  onNavigateHome: () => void;
  onNavigateToArticle: (article: NewsItem) => void;
  onNavigate?: (page: string) => void;
}

const NotFoundPage: React.FC<NotFoundPageProps> = ({ 
  brokenSlug, 
  news, 
  products, 
  onNavigateHome, 
  onNavigateToArticle,
  onNavigate
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Define Meta Robots noindex, follow para não prejudicar SEO no Google
  useEffect(() => {
    document.title = "Conteúdo Indisponível ou Movido | Busca Inteligente";
    
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    const previousRobots = metaRobots.getAttribute('content');
    metaRobots.setAttribute('content', 'noindex, follow');

    return () => {
      if (previousRobots) {
        metaRobots?.setAttribute('content', previousRobots);
      } else {
        metaRobots?.remove();
      }
    };
  }, []);
  
  // Lógica de Recomendação Inteligente + Busca ao Vivo
  const filteredResults = useMemo(() => {
    if (searchTerm.trim().length > 1) {
      const query = searchTerm.toLowerCase().trim();
      const matchedNews = news.filter(n => 
        (n.title + ' ' + (n.excerpt || '') + ' ' + (n.category || '')).toLowerCase().includes(query)
      ).slice(0, 4).map(n => ({ type: 'news' as const, data: n }));

      const matchedProducts = products.filter(p => 
        (p.title + ' ' + (p.description || '')).toLowerCase().includes(query)
      ).slice(0, 4).map(p => ({ type: 'product' as const, data: p }));

      return [...matchedNews, ...matchedProducts];
    }

    // 1. Limpar o slug para palavras-chave (remove traços e palavras comuns)
    const keywords = (brokenSlug || '')
      .replace(/-/g, ' ')
      .toLowerCase()
      .split(' ')
      .filter(w => w.length > 3 && !['para', 'com', 'dos', 'das', 'uma', 'que', 'news', 'artigo', 'guia', 'produtos'].includes(w));

    // 2. Buscar em Notícias
    const matchedNews = news.filter(n => {
      const text = (n.title + ' ' + (n.category || '')).toLowerCase();
      return keywords.some(k => text.includes(k));
    }).slice(0, 2);

    // 3. Buscar em Produtos
    const matchedProducts = products.filter(p => {
      const text = (p.title + ' ' + (p.description || '')).toLowerCase();
      return keywords.some(k => text.includes(k));
    }).slice(0, 2);

    // 4. Se não tiver matches suficientes, preencher com os mais recentes/destaques
    let results: Array<{ type: 'news' | 'product', data: any }> = [
        ...matchedNews.map(n => ({ type: 'news' as const, data: n })),
        ...matchedProducts.map(p => ({ type: 'product' as const, data: p }))
    ];

    if (results.length < 4) {
       const remainingSlots = 4 - results.length;
       const latestNews = news
        .filter(n => !results.some(r => r.type === 'news' && r.data.id === n.id))
        .slice(0, remainingSlots)
        .map(n => ({ type: 'news' as const, data: n }));
       
       results = [...results, ...latestNews];
    }

    return results;
  }, [brokenSlug, searchTerm, news, products]);

  const cleanSlugDisplay = useMemo(() => {
    return (brokenSlug || '').replace(/[-_]/g, ' ');
  }, [brokenSlug]);

  return (
    <div className="min-h-screen bg-[#f8fafc] pt-28 pb-20 px-4 animate-fade-in-up flex flex-col items-center">
      
      {/* 404 Hero Section */}
      <div className="max-w-2xl w-full text-center space-y-6 mb-12">
        <div className="relative inline-block">
            <div className="absolute inset-0 bg-amber-200 rounded-full blur-2xl opacity-50 animate-pulse"></div>
            <div className="relative bg-white p-6 rounded-3xl shadow-xl border border-amber-100 inline-flex text-amber-500">
                <FileQuestion size={56} />
            </div>
        </div>
        
        <div>
          <span className="px-3.5 py-1 bg-amber-50 text-amber-700 font-bold text-xs rounded-full inline-block mb-3 border border-amber-200">
            Redirecionamento Inteligente SEO
          </span>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight">
              Conteúdo Indisponível ou Movido
          </h1>
        </div>
        
        <div className="bg-white border border-slate-200 p-5 rounded-2xl max-w-lg mx-auto shadow-sm flex items-start gap-3 text-left">
            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            <div>
                <p className="text-slate-700 font-medium text-sm leading-relaxed">
                    O endereço que você tentou acessar (<span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-amber-600 font-bold break-all">/{brokenSlug}</span>) pode ter sido atualizado, renomeado ou movido para uma nova seção do portal.
                </p>
            </div>
        </div>

        {/* Instant Search Form */}
        <div className="max-w-md mx-auto relative">
          <div className="relative flex items-center">
            <Search className="absolute left-4 text-slate-400" size={18} />
            <input 
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquise o assunto ou matéria desejada..."
              className="w-full bg-white border border-slate-200 rounded-2xl pl-11 pr-4 py-3.5 text-sm font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm transition-all"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 text-xs text-slate-400 hover:text-slate-600 font-bold px-2 py-1 bg-slate-100 rounded-lg cursor-pointer"
              >
                Limpar
              </button>
            )}
          </div>
        </div>

        {/* Quick Action Navigation Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <button 
                onClick={onNavigateHome}
                className="px-5 py-3 bg-slate-900 text-white rounded-xl font-bold text-xs hover:bg-primary transition-all shadow-md flex items-center gap-2 cursor-pointer"
            >
                <Home size={15} /> Início
            </button>
            
            <button 
                onClick={() => onNavigate ? onNavigate('blog') : onNavigateHome()}
                className="px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-xs hover:border-primary hover:text-primary transition-all shadow-sm flex items-center gap-2 cursor-pointer"
            >
                <Newspaper size={15} /> Blog & Notícias
            </button>

            <button 
                onClick={() => onNavigate ? onNavigate('guia') : onNavigateHome()}
                className="px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-xs hover:border-primary hover:text-primary transition-all shadow-sm flex items-center gap-2 cursor-pointer"
            >
                <Compass size={15} /> Guia de Empresas
            </button>

            <button 
                onClick={() => onNavigate ? onNavigate('produtos') : onNavigateHome()}
                className="px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-xs hover:border-primary hover:text-primary transition-all shadow-sm flex items-center gap-2 cursor-pointer"
            >
                <ShoppingBag size={15} /> Produtos
            </button>
        </div>
      </div>

      {/* Suggestion Section */}
      <div className="max-w-6xl w-full">
        <div className="flex items-center gap-4 mb-8">
            <div className="h-[1px] flex-1 bg-slate-200"></div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                {searchTerm 
                  ? `Resultados para "${searchTerm}"` 
                  : cleanSlugDisplay 
                    ? `Conteúdo Relacionado a "${cleanSlugDisplay}"` 
                    : "Conteúdos Recomendados"}
            </span>
            <div className="h-[1px] flex-1 bg-slate-200"></div>
        </div>

        {filteredResults.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-3xl border border-slate-100 shadow-sm p-8 max-w-md mx-auto">
            <p className="text-slate-500 font-medium text-sm mb-4">Nenhum resultado encontrado para a sua busca.</p>
            <button 
              onClick={onNavigateHome}
              className="px-6 py-2.5 bg-primary text-white rounded-xl text-xs font-bold shadow-md hover:bg-primary/90 cursor-pointer"
            >
              Explorar Todos os Conteúdos
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredResults.map((item, idx) => (
                  <div 
                      key={idx} 
                      onClick={() => {
                          if (item.type === 'news') onNavigateToArticle(item.data);
                          else if (item.data.link) window.open(item.data.link, '_blank');
                      }}
                      className="bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-xl transition-all cursor-pointer group flex flex-col h-full"
                  >
                      <div className="h-40 overflow-hidden relative bg-slate-100">
                          <img 
                            src={item.data.imageUrl || item.data.image_url || 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?auto=format&fit=crop&w=600&q=80'} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                            alt={item.data.title} 
                          />
                          <div className="absolute top-3 left-3">
                              {item.type === 'news' ? (
                                  <span className="px-3 py-1 bg-white/90 backdrop-blur-md rounded-lg text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1 shadow-sm">
                                      <Newspaper size={10} /> Blog
                                  </span>
                              ) : (
                                  <span className="px-3 py-1 bg-slate-900/90 backdrop-blur-md rounded-lg text-[10px] font-black uppercase tracking-widest text-white flex items-center gap-1 shadow-sm">
                                      <Package size={10} /> Produto
                                  </span>
                              )}
                          </div>
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                          <h3 className="font-bold text-slate-900 mb-2 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                              {item.data.title}
                          </h3>
                          <p className="text-xs text-slate-500 font-medium line-clamp-2 mb-4 flex-1">
                              {item.type === 'news' ? item.data.excerpt : item.data.description}
                          </p>
                          <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest group-hover:text-primary transition-colors">
                              Acessar Conteúdo <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                          </div>
                      </div>
                  </div>
              ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default NotFoundPage;

